import { Component } from '@theme/component';

/**
 * @typedef {Object} PbDonationWallRefs
 * @property {HTMLElement} [list]
 * @property {HTMLElement} [loading]
 * @property {HTMLButtonElement} [more]
 * @property {HTMLElement} [summary]
 */

/**
 * Who's backing wall — fetches /apps/probros/backers?handle=…
 * Shows heading + loader + skeletons while fetching; hides if empty/error.
 * @extends {Component<PbDonationWallRefs>}
 */
class PbDonationWall extends Component {
  requiredRefs = ['list', 'loading', 'more', 'summary'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {object[]} */
  #backers = [];

  /** @type {boolean} */
  #showAll = false;

  /** @type {number} */
  #previewCount = 5;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const preview = Number.parseInt(this.dataset.previewCount || '5', 10);
    this.#previewCount = Number.isFinite(preview) && preview > 0 ? preview : 5;
    this.refs.more?.addEventListener('click', this.#onToggleMore, {
      signal: this.#abort.signal,
    });
    this.#load();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    super.disconnectedCallback();
  }

  #section() {
    return this.closest('.pb-donation-wall');
  }

  #hideSection() {
    const section = this.#section();
    if (section) section.hidden = true;
  }

  #showSection() {
    const section = this.#section();
    if (section) section.hidden = false;
  }

  #showLoading() {
    this.#showSection();
    this.refs.summary.hidden = true;
    this.refs.more.hidden = true;
    this.refs.loading.hidden = false;
    this.#renderSkeletons();
  }

  #hideLoading() {
    this.refs.loading.hidden = true;
  }

  #renderSkeletons() {
    const list = document.createDocumentFragment();
    list.appendChild(this.#skeleton({ quote: true, twoLines: false }));
    list.appendChild(this.#skeleton({ quote: false, twoLines: true }));
    this.refs.list.replaceChildren(list);
  }

  /**
   * @param {{ quote: boolean, twoLines: boolean }} opts
   * @returns {HTMLElement}
   */
  #skeleton(opts) {
    const card = document.createElement('div');
    card.className = 'pb-donation-wall__skeleton';
    card.setAttribute('aria-hidden', 'true');

    const top = document.createElement('div');
    top.className = 'pb-donation-wall__skeleton-top';

    const who = document.createElement('div');
    who.className = 'pb-donation-wall__skeleton-who';
    who.appendChild(this.#skel('name'));
    who.appendChild(this.#skel('ago'));

    top.appendChild(who);
    top.appendChild(this.#skel('amt'));
    card.appendChild(top);

    if (opts.twoLines) {
      const lines = document.createElement('div');
      lines.className = 'pb-donation-wall__skeleton-lines';
      lines.appendChild(this.#skel('line'));
      lines.appendChild(this.#skel('line', 'line-wide'));
      card.appendChild(lines);
    } else {
      card.appendChild(this.#skel('line'));
    }

    if (opts.quote) {
      card.appendChild(this.#skel('quote'));
    }

    return card;
  }

  /**
   * @param {string} name
   * @param {string} [extra]
   * @returns {HTMLElement}
   */
  #skel(name, extra) {
    const el = document.createElement('span');
    el.className = `pb-donation-wall__skel pb-donation-wall__skel--${name}`;
    if (extra) el.classList.add(`pb-donation-wall__skel--${extra}`);
    return el;
  }

  #onToggleMore = () => {
    this.#showAll = !this.#showAll;
    this.#render();
  };

  async #load() {
    const handle = (this.dataset.probroHandle || '').trim();
    const endpoint = this.dataset.backersUrl || '/apps/probros/backers';

    if (!handle) {
      this.#hideSection();
      return;
    }

    this.#showLoading();

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('handle', handle);

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.#abort?.signal,
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      this.#backers = Array.isArray(data.backers) ? data.backers : [];
      this.#showAll = false;
      this.#hideLoading();
      this.#render();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      this.#hideLoading();
      this.#hideSection();
    }
  }

  #render() {
    const backers = this.#backers;
    const count = backers.length;
    const showAllLabel = this.dataset.showAllLabel || 'Show all donations →';
    const showFewerLabel = this.dataset.showFewerLabel || 'Show fewer';
    const summaryTemplate =
      this.dataset.summaryTemplate || '[name] has raised [amount] from [count].';
    const broName = this.dataset.probroName || '';

    if (count === 0) {
      this.#hideSection();
      this.refs.summary.hidden = true;
      this.refs.more.hidden = true;
      this.refs.list.replaceChildren();
      return;
    }

    this.#showSection();

    const totalCents = backers.reduce(
      (sum, row) => sum + (Number(row.amount_cents) || 0),
      0,
    );
    const summary = summaryTemplate
      .replaceAll('[name]', broName)
      .replaceAll('[amount]', this.#formatMoney(totalCents))
      .replaceAll(
        '[count]',
        `${count} backer${count === 1 ? '' : 's'}`,
      );
    this.refs.summary.hidden = false;
    this.refs.summary.textContent = summary;

    const shown = this.#showAll ? backers : backers.slice(0, this.#previewCount);
    const list = document.createDocumentFragment();
    for (const row of shown) {
      list.appendChild(this.#card(row));
    }
    this.refs.list.replaceChildren(list);

    if (count > this.#previewCount) {
      this.refs.more.hidden = false;
      this.refs.more.textContent = this.#showAll ? showFewerLabel : showAllLabel;
    } else {
      this.refs.more.hidden = true;
    }
  }

  /**
   * @param {object} row
   * @returns {HTMLElement}
   */
  #card(row) {
    const article = document.createElement('article');
    article.className = 'pb-donation-wall__card';

    const top = document.createElement('div');
    top.className = 'pb-donation-wall__card-top';

    const whoWrap = document.createElement('div');
    whoWrap.className = 'pb-donation-wall__who-wrap';

    const who = document.createElement('span');
    who.className = 'pb-donation-wall__who';
    who.textContent = row.who || 'Anonymous';
    whoWrap.appendChild(who);

    const ago = document.createElement('span');
    ago.className = 'pb-donation-wall__ago';
    ago.textContent = this.#relativeTime(row.created_at);
    whoWrap.appendChild(ago);

    const amount = document.createElement('span');
    amount.className = 'pb-donation-wall__amount';
    amount.textContent = this.#formatMoney(row.amount_cents);

    top.appendChild(whoWrap);
    top.appendChild(amount);
    article.appendChild(top);

    const lines = Array.isArray(row.lines) ? row.lines.filter(Boolean) : [];
    if (lines.length) {
      const linesEl = document.createElement('div');
      linesEl.className = 'pb-donation-wall__lines';
      for (const line of lines) {
        const chip = document.createElement('span');
        chip.className = 'pb-donation-wall__line';
        chip.textContent = line;
        linesEl.appendChild(chip);
      }
      article.appendChild(linesEl);
    }

    if (row.msg) {
      const msgRow = document.createElement('div');
      msgRow.className = 'pb-donation-wall__msg-row';

      const quote = document.createElement('span');
      quote.className = 'pb-donation-wall__msg-quote';
      quote.setAttribute('aria-hidden', 'true');
      quote.textContent = '“';
      msgRow.appendChild(quote);

      const msg = document.createElement('p');
      msg.className = 'pb-donation-wall__msg';
      msg.textContent = row.msg;
      msgRow.appendChild(msg);

      article.appendChild(msgRow);
    }

    return article;
  }

  /**
   * Reference format: $30 (no NZD suffix).
   * @param {number|string|null|undefined} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    const rounded = value % 1 === 0 ? Math.round(value) : value;
    try {
      return new Intl.NumberFormat(this.dataset.locale || 'en-NZ', {
        style: 'currency',
        currency: 'NZD',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(rounded);
    } catch {
      return `$${Number(rounded).toLocaleString('en-NZ')}`;
    }
  }

  /**
   * @param {string|null|undefined} iso
   * @returns {string}
   */
  #relativeTime(iso) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';

    const seconds = Math.round((Date.now() - then) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) {
      const m = Math.floor(seconds / 60);
      return `${m} minute${m === 1 ? '' : 's'} ago`;
    }
    if (seconds < 86400) {
      const h = Math.floor(seconds / 3600);
      return `${h} hour${h === 1 ? '' : 's'} ago`;
    }
    if (seconds < 172800) return 'Yesterday';
    const d = Math.floor(seconds / 86400);
    if (d < 7) return `${d} days ago`;
    try {
      return new Intl.DateTimeFormat(this.dataset.locale || 'en-NZ', {
        day: 'numeric',
        month: 'short',
      }).format(new Date(then));
    } catch {
      return '';
    }
  }
}

if (!customElements.get('pb-donation-wall')) {
  customElements.define('pb-donation-wall', PbDonationWall);
}
